// src/components/Header.js
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styles from "../css/shared.module.css"; // ✅ Apunta al nuevo CSS unificado
import {
  isAuthenticated,
  getUserRole,
  getUserEmail,
  logout,
} from "../utils/auth";
import { getUserGpts } from "../services/api";
import logo from "../assets/logo.jpeg";
import { FiLogOut, FiChevronDown, FiMessageCircle, FiShield, FiUsers, FiEye, FiCpu, FiClock, FiSettings } from "react-icons/fi";

const Header = () => {
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [email, setEmail] = useState(null);
  const [gpts, setGpts] = useState([]);
  const [isGptsDropdownOpen, setIsGptsDropdownOpen] = useState(false);
  const [isLoadingGpts, setIsLoadingGpts] = useState(false);
  const [isAdminDropdownOpen, setIsAdminDropdownOpen] = useState(false);

  useEffect(() => {
    if (isAuthenticated()) {
      setRole(getUserRole());
      setEmail(getUserEmail());
      loadUserGpts();
    }
  }, []);

  // Escuchar eventos de actualización de GPTs
  useEffect(() => {
    const handleGptsUpdate = () => {
      console.log("🔄 [Header] Evento de actualización de GPTs recibido, recargando...");
      loadUserGpts();
    };

    // Escuchar evento personalizado
    window.addEventListener('gptsUpdated', handleGptsUpdate);
    
    return () => {
      window.removeEventListener('gptsUpdated', handleGptsUpdate);
    };
  }, []);

  // Cerrar dropdowns al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isGptsDropdownOpen && !event.target.closest(`.${styles.gptsDropdown}`)) {
        closeGptsDropdown();
      }
      if (isAdminDropdownOpen && !event.target.closest(`.${styles.adminDropdown}`)) {
        setIsAdminDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isGptsDropdownOpen, isAdminDropdownOpen]);

  // Cargar GPTs del usuario
  const loadUserGpts = async () => {
    try {
      setIsLoadingGpts(true);
      const response = await getUserGpts();
      if (response.success) {
        setGpts(response.data);
      }
    } catch (error) {
      console.error("Error loading user GPTs:", error);
    } finally {
      setIsLoadingGpts(false);
    }
  };

  // Manejar apertura/cierre del dropdown
  const toggleGptsDropdown = () => {
    setIsGptsDropdownOpen(!isGptsDropdownOpen);
  };

  // Cerrar dropdown al hacer click fuera
  const closeGptsDropdown = () => {
    setIsGptsDropdownOpen(false);
  };

  // Abrir chat con un GPT específico
  const handleOpenChat = (gptId) => {
    navigate(`/gpt/${gptId}`);
    setIsGptsDropdownOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // El guard de autenticación es una excelente práctica
  if (!isAuthenticated()) return null;

  return (
    <header className={styles.header}>
      {/* Sección Izquierda: Branding */}
      <Link to="/dashboard" className={styles.branding}>
        <img src={logo} alt="AI Community PRO Logo" className={styles.logo} />
        <span>Mentor IA</span>
      </Link>

      {/* Sección Central: Navegación Principal */}
      <nav className={styles.navLinks}>
        <Link to="/dashboard" className={styles.navLink}>
          Inicio
        </Link>
        <Link to="/brain" className={styles.navLink}>
          <FiCpu size={16} />
          <span>Mi Cerebro</span>
        </Link>
        <div className={styles.gptsDropdown}>
          <button
            className={`${styles.navLink} ${styles.dropdownButton}`}
            onClick={toggleGptsDropdown}
          >
            <FiMessageCircle size={16} />
            <span>Mis GPTs</span>
            <FiChevronDown 
              size={14} 
              className={`${styles.dropdownArrow} ${isGptsDropdownOpen ? styles.dropdownArrowOpen : ''}`}
            />
          </button>
          
          {isGptsDropdownOpen && (
            <div className={styles.dropdownMenu}>
              <div className={styles.dropdownHeader}>
                <h4>GPTs Disponibles</h4>
                <span className={styles.gptsCount}>{gpts.length} GPTs</span>
              </div>
              
              {isLoadingGpts ? (
                <div className={styles.dropdownLoading}>
                  <div className={styles.spinner} />
                  <span>Cargando...</span>
                </div>
              ) : gpts.length === 0 ? (
                <div className={styles.dropdownEmpty}>
                  <FiMessageCircle size={20} />
                  <span>No tienes GPTs asignados</span>
                </div>
              ) : (
                <div className={styles.dropdownList}>
                  {gpts.map((gpt) => (
                    <button
                      key={gpt._id}
                      className={styles.dropdownItem}
                      onClick={() => handleOpenChat(gpt._id)}
                    >
                      <div className={styles.dropdownItemContent}>
                        <h5>{gpt.name}</h5>
                        <p>{gpt.description || "Sin descripción"}</p>
                      </div>
                      <FiMessageCircle size={16} />
                    </button>
                  ))}
                </div>
              )}
              
              <div className={styles.dropdownFooter}>
                <button
                  className={styles.dropdownFooterButton}
                  onClick={() => {
                    navigate('/dashboard');
                    closeGptsDropdown();
                  }}
                >
                  Ver todos en Dashboard
                </button>
              </div>
            </div>
          )}
        </div>
        {role === "admin" && (
          <div className={styles.adminDropdown}>
            <button
              className={`${styles.navLink} ${styles.dropdownButton}`}
              onClick={() => setIsAdminDropdownOpen(!isAdminDropdownOpen)}
            >
              <FiShield size={16} />
              <span>Panel Admin</span>
              <FiChevronDown 
                size={14} 
                className={`${styles.dropdownArrow} ${isAdminDropdownOpen ? styles.dropdownArrowOpen : ''}`}
              />
            </button>
            
            {isAdminDropdownOpen && (
              <div className={styles.dropdownMenu}>
                <div className={styles.dropdownHeader}>
                  <h4>Panel de Administración</h4>
                  <span className={styles.adminBadge}>Admin</span>
                </div>
                
                <div className={styles.dropdownList}>
                  {/* Dashboard Completo */}
                  <button
                    className={styles.dropdownItem}
                    onClick={() => {
                      navigate('/admin');
                      setIsAdminDropdownOpen(false);
                    }}
                  >
                    <div className={styles.dropdownItemContent}>
                      <h5>Dashboard Completo</h5>
                      <p>Acceso a todos los paneles administrativos</p>
                    </div>
                    <FiShield size={16} />
                  </button>

                  {/* Separador visual */}
                  <div className={styles.dropdownSeparator}>
                    <span>Gestión de GPTs</span>
                  </div>

                  {/* Gestión de GPTs */}
                  <button
                    className={styles.dropdownItem}
                    onClick={() => {
                      navigate('/admin');
                      setTimeout(() => {
                        const element = document.getElementById('panel-manage-gpts');
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }, 100);
                      setIsAdminDropdownOpen(false);
                    }}
                  >
                    <div className={styles.dropdownItemContent}>
                      <h5>Gestión de GPTs</h5>
                      <p>Crear, editar y eliminar asistentes de IA</p>
                    </div>
                    <FiSettings size={16} />
                  </button>

                  {/* Acceso a GPTs */}
                  <button
                    className={styles.dropdownItem}
                    onClick={() => {
                      navigate('/admin');
                      setTimeout(() => {
                        const element = document.getElementById('panel-access');
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }, 100);
                      setIsAdminDropdownOpen(false);
                    }}
                  >
                    <div className={styles.dropdownItemContent}>
                      <h5>Acceso a GPTs</h5>
                      <p>Otorgar o revocar acceso de usuarios a GPTs</p>
                    </div>
                    <FiShield size={16} />
                  </button>

                  {/* Separador visual */}
                  <div className={styles.dropdownSeparator}>
                    <span>Gestión de Usuarios</span>
                  </div>

                  {/* Gestión de Usuarios */}
                  <button
                    className={styles.dropdownItem}
                    onClick={() => {
                      navigate('/admin');
                      setTimeout(() => {
                        const element = document.getElementById('panel-create-user');
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }, 100);
                      setIsAdminDropdownOpen(false);
                    }}
                  >
                    <div className={styles.dropdownItemContent}>
                      <h5>Gestión de Usuarios</h5>
                      <p>Crear usuarios y cambiar contraseñas</p>
                    </div>
                    <FiUsers size={16} />
                  </button>

                  {/* Ver Accesos de Usuario */}
                  <button
                    className={styles.dropdownItem}
                    onClick={() => {
                      navigate('/admin');
                      setTimeout(() => {
                        const element = document.getElementById('panel-view-access');
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }, 100);
                      setIsAdminDropdownOpen(false);
                    }}
                  >
                    <div className={styles.dropdownItemContent}>
                      <h5>Ver Accesos de Usuario</h5>
                      <p>Consultar y gestionar accesos de usuarios</p>
                    </div>
                    <FiEye size={16} />
                  </button>

                  {/* Gestión de Expiraciones */}
                  <button
                    className={styles.dropdownItem}
                    onClick={() => {
                      navigate('/admin');
                      setTimeout(() => {
                        // Abrir el modal de gestión de expiraciones
                        const event = new CustomEvent('openExpirationModal', { 
                          detail: { tab: 'all' } 
                        });
                        window.dispatchEvent(event);
                      }, 100);
                      setIsAdminDropdownOpen(false);
                    }}
                  >
                    <div className={styles.dropdownItemContent}>
                      <h5>Gestión de Expiraciones</h5>
                      <p>Gestionar fechas de expiración de acceso</p>
                    </div>
                    <FiClock size={16} />
                  </button>
                </div>
                
                <div className={styles.dropdownFooter}>
                  <button
                    className={styles.dropdownFooterButton}
                    onClick={() => {
                      navigate('/admin');
                      setIsAdminDropdownOpen(false);
                    }}
                  >
                    Abrir Panel Completo
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Sección Derecha: Acciones del Usuario */}
      <div className={styles.userActions}>
        {email && <span className={styles.userEmail}>{email}</span>}
        <Link to="/change-password" className={styles.navLink}>
          Perfil
        </Link>
        <button
          onClick={handleLogout}
          className={`${styles.button} ${styles.logoutButton}`}
        >
          <FiLogOut />
          <span style={{ display: "none", sm: "inline" }}>Cerrar sesión</span>
        </button>
      </div>
    </header>
  );
};

export default Header;
